export const RICH_CONTENT_ALLOWED_TAGS = [
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'p', 'div', 'span', 'section', 'article', 'aside',
  'ul', 'ol', 'li', 'dl', 'dt', 'dd',
  'a', 'strong', 'em', 'u', 's', 'sub', 'sup', 'small', 'mark',
  'table', 'caption', 'thead', 'tbody', 'tr', 'th', 'td',
  'figure', 'figcaption', 'img', 'iframe',
  'blockquote', 'pre', 'code', 'br', 'hr'
] as const;

export const RICH_CONTENT_ALLOWED_ATTRIBUTES = {
  '*': ['class', 'id', 'title', 'style'],
  a: ['href', 'target', 'rel'],
  img: ['src', 'alt'],
  th: ['colspan', 'rowspan'],
  td: ['colspan', 'rowspan'],
  iframe: [
    'src', 'title', 'width', 'height', 'allow', 'allowfullscreen',
    'loading', 'referrerpolicy', 'sandbox'
  ]
} as const satisfies Readonly<Record<string, readonly string[]>>;

export const RICH_CONTENT_SAFE_CSS_PROPERTIES = [
  'color',
  'background-color',
  'text-align',
  'font-size',
  'font-weight',
  'font-style',
  'text-decoration',
  'margin*',
  'padding*',
  'border*',
  'width',
  'max-width',
  'height',
  'display',
  'gap',
  'flex*',
  'grid*',
  'line-height'
] as const;

export const RICH_CONTENT_FORBIDDEN_TAGS = [
  'script', 'style', 'link', 'meta', 'base', 'object', 'embed',
  'form', 'input', 'button', 'textarea', 'select',
  'svg', 'math', 'template', 'noscript'
] as const;

export const RICH_CONTENT_IFRAME_FORCED_ATTRIBUTES = {
  sandbox: 'allow-scripts allow-same-origin allow-forms allow-popups allow-presentation',
  referrerpolicy: 'strict-origin-when-cross-origin',
  loading: 'lazy'
} as const;

export const RICH_CONTENT_ALLOWED_ATTRIBUTE_NAMES = Array.from(new Set(
  Object.values(RICH_CONTENT_ALLOWED_ATTRIBUTES).flat()
));

const SAFE_ID_PATTERN = /^[a-z][a-z0-9_-]{0,79}$/;
const UNSAFE_CSS_VALUE = /(?:url\s*\(|expression\s*\(|@import|\/\*|\*\/|[\\\u0000-\u001f\u007f])/i;

function isAllowedCssProperty(property: string): boolean {
  return RICH_CONTENT_SAFE_CSS_PROPERTIES.some((allowed) => allowed.endsWith('*')
    ? property.startsWith(allowed.slice(0, -1))
    : property === allowed);
}

export function filterRichContentStyle(style: string): string | null {
  const declarations = style
    .split(';')
    .map((declaration) => declaration.trim())
    .filter(Boolean)
    .flatMap((declaration) => {
      const separator = declaration.indexOf(':');
      if (separator <= 0) return [];
      const property = declaration.slice(0, separator).trim().toLowerCase();
      const value = declaration.slice(separator + 1).trim();
      if (!value || property === 'position' || property === 'behavior') return [];
      if (!isAllowedCssProperty(property) || UNSAFE_CSS_VALUE.test(value)) return [];
      return [`${property}: ${value}`];
    });
  return declarations.length > 0 ? declarations.join('; ') : null;
}

export function sanitizeRichContentId(id: string): string | null {
  let sanitized = id
    .normalize('NFKD')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^[-_]+|[-_]+$/g, '');
  if (!sanitized) return null;
  if (!/^[a-z]/.test(sanitized)) sanitized = `section-${sanitized}`;
  sanitized = sanitized.slice(0, 80).replace(/[-_]+$/g, '');
  return SAFE_ID_PATTERN.test(sanitized) ? sanitized : null;
}

export function isAllowedRichContentIframeSrc(src: string): boolean {
  const normalized = src.trim();
  if (!/^https:\/\//i.test(normalized)) return false;
  const UrlConstructor = (globalThis as unknown as {
    URL?: new (value: string) => { protocol: string; hostname: string; username: string; password: string };
  }).URL;
  if (!UrlConstructor) return false;
  try {
    const url = new UrlConstructor(normalized);
    return url.protocol === 'https:' && Boolean(url.hostname) && !url.username && !url.password;
  } catch {
    return false;
  }
}
